/**
 * Ethnicwear (India) silhouettes: kurta, kurti, saree_blouse, lehenga,
 * sherwani, nehru_jacket, salwar, churidar.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";
import { partGroup } from "./_part-attrs.js";
import { mandarinCollar, placketButtons, neckLabel, elasticGather } from "./_primitives.js";

import type { FlatView } from "../flat-svg.js";

/* ── Kurta (long tunic, mandarin collar, side slits) ─────────────── */
export function kurtaFlat(view: FlatView): string {
  return `<g>
  ${partGroup("body", `<path ${OUTLINE_ATTRS} d="M 162 84 L 112 100 L 72 230 L 54 334 L 102 348 L 120 266 L 130 180 L 122 456 Q 200 468 278 456 L 270 180 L 280 266 L 298 348 L 346 334 L 328 230 L 288 100 L 238 84 Q 200 72 162 84 Z"/>
  <path ${DETAIL_ATTRS} d="M 122 410 L 122 456"/>
  <path ${DETAIL_ATTRS} d="M 278 410 L 278 456"/>
  ${view === "back" ? `<path ${STITCH_ATTRS} d="M 136 130 L 264 130"/>
  ${neckLabel(200, 68)}` : ""}`)}
  ${partGroup("collar", mandarinCollar(view, 172, 228, 86))}
  ${view === "front" ? partGroup("placket", `<path ${DETAIL_ATTRS} d="M 194 86 L 192 220"/>
  <path ${DETAIL_ATTRS} d="M 206 86 L 208 220"/>
  <path ${STITCH_ATTRS} d="M 198 90 L 196 216"/>
  <path ${STITCH_ATTRS} d="M 202 90 L 204 216"/>
  ${placketButtons(200, [110, 140, 170, 200])}`) : ""}
  ${partGroup("sleeve_left", `<path ${DETAIL_FILLED_ATTRS} d="M 54 334 L 102 348 L 96 370 L 48 356 Z"/>
  <path ${STITCH_ATTRS} d="M 112 102 Q 124 140 130 180"/>`)}
  ${partGroup("sleeve_right", `<path ${DETAIL_FILLED_ATTRS} d="M 346 334 L 298 348 L 304 370 L 352 356 Z"/>
  <path ${STITCH_ATTRS} d="M 288 102 Q 276 140 270 180"/>`)}
  ${partGroup("hem", `<path ${STITCH_ATTRS} d="M 124 446 Q 200 458 276 446"/>`)}
</g>`;
}

/* ── Kurti (shorter kurta, knee-length, mandarin collar) ──────────── */
export function kurtiFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 160 82 L 112 98 L 74 226 L 56 328 L 104 342 L 120 262 L 130 178 L 126 430 Q 200 442 274 430 L 270 178 L 280 262 L 296 342 L 344 328 L 326 226 L 288 98 L 240 82 Q 200 70 160 82 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 56 328 L 104 342 L 98 364 L 50 350 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 344 328 L 296 342 L 302 364 L 350 350 Z"/>
  <path ${STITCH_ATTRS} d="M 112 100 Q 124 138 130 178"/>
  <path ${STITCH_ATTRS} d="M 288 100 Q 276 138 270 178"/>
  ${mandarinCollar(view, 170, 230, 84)}
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 194 84 L 192 210"/>
  <path ${DETAIL_ATTRS} d="M 206 84 L 208 210"/>
  <path ${STITCH_ATTRS} d="M 198 88 L 196 206"/>
  <path ${STITCH_ATTRS} d="M 202 88 L 204 206"/>
  ${placketButtons(200, [108, 136, 164, 192])}`
      : `<path ${STITCH_ATTRS} d="M 136 126 L 264 126"/>
  ${neckLabel(200, 66)}`
  }
  <path ${STITCH_ATTRS} d="M 128 420 Q 200 432 272 420"/>
  <path ${STITCH_ATTRS} d="M 126 426 Q 200 438 274 426"/>
</g>`;
}

/* ── Saree blouse (short, fitted, back opening) ──────────────────── */
export function saree_blouseFlat(view: FlatView): string {
  const neckDip = view === "front" ? 128 : 102;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 160 82 L 108 100 L 62 190 L 80 210 L 130 186 L 136 300 Q 200 312 264 300 L 270 186 L 320 210 L 338 190 L 292 100 L 240 82 Q 200 ${neckDip - 24} 160 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 160 82 Q 200 ${neckDip} 240 82"/>
  <path ${STITCH_ATTRS} d="M 163 87 Q 200 ${neckDip + 6} 237 87"/>
  <path ${DETAIL_ATTRS} d="M 62 190 L 80 210"/>
  <path ${DETAIL_ATTRS} d="M 338 190 L 320 210"/>
  <path ${STITCH_ATTRS} d="M 69 186 L 86 204"/>
  <path ${STITCH_ATTRS} d="M 331 186 L 314 204"/>
  <path ${STITCH_ATTRS} d="M 108 102 Q 122 142 130 186"/>
  <path ${STITCH_ATTRS} d="M 292 102 Q 278 142 270 186"/>
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 168 148 L 164 290"/>
  <path ${STITCH_ATTRS} d="M 232 148 L 236 290"/>
  <rect x="186" y="92" width="28" height="12" rx="1.5" ${DETAIL_ATTRS}/>`
      : `<path ${DETAIL_ATTRS} d="M 194 92 L 194 296"/>
  <path ${DETAIL_ATTRS} d="M 206 92 L 206 296"/>
  <path ${STITCH_ATTRS} d="M 198 96 L 198 292"/>
  <path ${STITCH_ATTRS} d="M 202 96 L 202 292"/>
  <circle cx="200" cy="140" r="3" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="200" cy="170" r="3" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="200" cy="200" r="3" ${DETAIL_FILLED_ATTRS}/>
  ${neckLabel(200, 82)}`
  }
  <path ${STITCH_ATTRS} d="M 138 292 Q 200 304 262 292"/>
</g>`;
}

/* ── Lehenga (long flared skirt, high waist, drawstring) ──────────── */
export function lehengaFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 80 L 242 80 L 248 108 L 310 460 Q 200 476 90 460 L 152 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 152 108 L 248 108"/>
  ${elasticGather(90, 164, 236)}
  <path ${STITCH_ATTRS} d="M 160 84 L 240 84"/>
  <path ${STITCH_ATTRS} d="M 154 104 L 246 104"/>
  <path ${DETAIL_ATTRS} d="M 192 88 L 188 106"/>
  <path ${DETAIL_ATTRS} d="M 208 88 L 212 106"/>
  <rect x="185" y="102" width="6" height="12" rx="1.5" ${DETAIL_FILLED_ATTRS}/>
  <rect x="209" y="102" width="6" height="12" rx="1.5" ${DETAIL_FILLED_ATTRS}/>
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 166 120 L 128 452"/>
  <path ${STITCH_ATTRS} d="M 200 108 L 200 460"/>
  <path ${STITCH_ATTRS} d="M 234 120 L 272 452"/>`
      : `<path ${DETAIL_ATTRS} d="M 200 108 L 200 462"/>
  <path ${STITCH_ATTRS} d="M 196 112 L 194 458"/>
  <path ${STITCH_ATTRS} d="M 204 112 L 206 458"/>`
  }
  <path ${STITCH_ATTRS} d="M 94 452 Q 200 468 306 452"/>
  <path ${STITCH_ATTRS} d="M 92 456 Q 200 472 308 456"/>
</g>`;
}

/* ── Sherwani (long coat-like, mandarin collar, front buttons) ────── */
export function sherwaniFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 160 84 L 108 102 L 66 230 L 46 340 L 100 354 L 118 268 L 130 182 L 120 458 Q 200 470 280 458 L 270 182 L 282 268 L 300 354 L 354 340 L 334 230 L 292 102 L 240 84 Q 200 72 160 84 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 46 340 L 100 354 L 94 376 L 40 362 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 354 340 L 300 354 L 306 376 L 360 362 Z"/>
  <path ${STITCH_ATTRS} d="M 108 104 Q 122 142 130 182"/>
  <path ${STITCH_ATTRS} d="M 292 104 Q 278 142 270 182"/>
  ${mandarinCollar(view, 170, 230, 86)}
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 194 86 L 190 452"/>
  <path ${DETAIL_ATTRS} d="M 206 86 L 210 452"/>
  <path ${STITCH_ATTRS} d="M 198 90 L 194 448"/>
  <path ${STITCH_ATTRS} d="M 202 90 L 206 448"/>
  ${placketButtons(200, [110, 150, 190, 230, 270, 310, 350])}`
      : `<path ${DETAIL_ATTRS} d="M 200 68 L 200 454"/>
  <path ${STITCH_ATTRS} d="M 136 130 L 264 130"/>
  ${neckLabel(200, 68)}`
  }
  <path ${STITCH_ATTRS} d="M 122 448 Q 200 460 278 448"/>
</g>`;
}

/* ── Nehru jacket (sleeveless, mandarin collar, button front) ────── */
export function nehru_jacketFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 162 84 Q 200 72 238 84 L 268 92 Q 284 106 278 134 Q 272 148 262 146 L 262 424 Q 200 436 138 424 L 138 146 Q 128 148 122 134 Q 116 106 132 92 Z"/>
  <path ${STITCH_ATTRS} d="M 118 128 Q 130 144 142 140"/>
  <path ${STITCH_ATTRS} d="M 282 128 Q 270 144 258 140"/>
  ${mandarinCollar(view, 172, 228, 92)}
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 194 92 L 192 420"/>
  <path ${DETAIL_ATTRS} d="M 206 92 L 208 420"/>
  <path ${STITCH_ATTRS} d="M 198 96 L 196 418"/>
  <path ${STITCH_ATTRS} d="M 202 96 L 204 418"/>
  ${placketButtons(200, [114, 150, 186, 222, 258, 294, 330])}`
      : `<path ${DETAIL_ATTRS} d="M 200 74 L 200 420"/>
  <path ${STITCH_ATTRS} d="M 146 126 L 254 126"/>
  ${neckLabel(200, 74)}`
  }
  <path ${STITCH_ATTRS} d="M 140 416 Q 200 428 260 416"/>
</g>`;
}

/* ── Salwar (loose pants, gathered at ankle) ──────────────────────── */
export function salwarFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 142 80 L 258 80 L 264 108 L 280 310 Q 260 340 240 380 Q 226 420 224 440 L 176 440 Q 174 420 160 380 Q 140 340 120 310 L 136 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 136 108 L 264 108"/>
  ${elasticGather(90, 148, 252)}
  <path ${STITCH_ATTRS} d="M 140 84 L 260 84"/>
  <path ${STITCH_ATTRS} d="M 138 104 L 262 104"/>
  <path ${DETAIL_ATTRS} d="M 192 88 L 188 106"/>
  <path ${DETAIL_ATTRS} d="M 208 88 L 212 106"/>
  <rect x="185" y="102" width="6" height="12" rx="1.5" ${DETAIL_FILLED_ATTRS}/>
  <rect x="209" y="102" width="6" height="12" rx="1.5" ${DETAIL_FILLED_ATTRS}/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 190"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 190 148 192 192"/>
  <path ${STITCH_ATTRS} d="M 150 180 L 134 302"/>
  <path ${STITCH_ATTRS} d="M 250 180 L 266 302"/>`
      : `<path ${DETAIL_ATTRS} d="M 140 148 Q 200 166 260 148"/>
  <path ${STITCH_ATTRS} d="M 142 152 Q 200 170 258 152"/>`
  }
  ${elasticGather(432, 178, 222)}
</g>`;
}

/* ── Churidar (tight pants, gathered/ruched below knee) ──────────── */
export function churidarFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 148 80 L 252 80 L 258 108 L 264 280 Q 252 310 244 350 L 240 440 L 160 440 L 156 350 Q 148 310 136 280 L 142 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 142 108 L 258 108"/>
  ${elasticGather(90, 154, 246)}
  <path ${STITCH_ATTRS} d="M 150 84 L 250 84"/>
  <path ${STITCH_ATTRS} d="M 144 104 L 256 104"/>
  <path ${DETAIL_ATTRS} d="M 192 88 L 188 106"/>
  <path ${DETAIL_ATTRS} d="M 208 88 L 212 106"/>
  <rect x="185" y="102" width="6" height="12" rx="1.5" ${DETAIL_FILLED_ATTRS}/>
  <rect x="209" y="102" width="6" height="12" rx="1.5" ${DETAIL_FILLED_ATTRS}/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 188"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 190 148 192 190"/>`
      : `<path ${DETAIL_ATTRS} d="M 144 146 Q 200 162 256 146"/>
  <path ${STITCH_ATTRS} d="M 146 150 Q 200 166 254 150"/>`
  }
  ${elasticGather(300, 140, 176)}
  ${elasticGather(316, 142, 174)}
  ${elasticGather(332, 144, 172)}
  ${elasticGather(300, 224, 260)}
  ${elasticGather(316, 226, 258)}
  ${elasticGather(332, 228, 256)}
  <path ${STITCH_ATTRS} d="M 162 432 L 238 432"/>
</g>`;
}
